import {
  CollectionReference,
  DocumentReference,
  QueryDocumentSnapshot,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
  type DocumentData,
  type QueryConstraint,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import {
  type CreateUserData,
  type UpdateUserData,
  type User,
  type UserFilters,
} from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";
import type { UserServiceFilters } from "./userService";

/**
 * Referencia a la colección de usuarios
 * @constant {string} USERS_COLLECTION - La referencia a la colección de usuarios
 */
const USERS_COLLECTION = "users";

/**
 * Función para obtener la referencia a la colección de usuarios
 * @returns {CollectionReference<DocumentData>} La referencia a la colección de usuarios
 */
function getUsersCollection(): CollectionReference<DocumentData> {
  return collection(db, USERS_COLLECTION);
}

/**
 * Función para obtener la referencia a un documento de usuario
 * @param userId - El ID del usuario
 * @returns {DocumentReference<DocumentData>} La referencia al documento del usuario
 */
function getUserDoc(userId: string): DocumentReference<DocumentData> {
  return doc(db, USERS_COLLECTION, userId);
}

/**
 * Función para convertir DocumentData a User
 * @param doc - El documento a convertir
 * @returns {User} El usuario convertido
 */
function docToUser(doc: QueryDocumentSnapshot<DocumentData>): User {
  return doc.data() as User;
}

/**
 * Función para crear un nuevo usuario
 * @param userData - Los datos del usuario a crear
 * @returns {Promise<User>} El usuario creado
 */
export async function createUser(userData: CreateUserData): Promise<User> {
  try {
    const now = new Date().toISOString();
    const userWithTimestamps = {
      ...userData,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(getUsersCollection(), userWithTimestamps);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Error al crear el usuario");
    }

    return docToUser(docSnap);
  } catch (error) {
    throw new Error(
      `Error al crear usuario: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}

/**
 * Función para obtener un usuario por ID
 * @param userId - El ID del usuario
 * @returns {Promise<User | null>} El usuario encontrado o null si no existe
 */
export async function getUserById(userId: string): Promise<User | null> {
  try {
    const docSnap = await getDoc(getUserDoc(userId));

    if (!docSnap.exists()) {
      return null;
    }

    return docToUser(docSnap as QueryDocumentSnapshot<DocumentData, User>);
  } catch (error) {
    throw new Error(
      `Error al obtener usuario: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}

/**
 * Función para obtener todos los usuarios
 * @returns {Promise<User[]>} Los usuarios obtenidos
 */
export async function getAllUsers(): Promise<User[]> {
  try {
    const querySnapshot = await getDocs(getUsersCollection());
    return querySnapshot.docs.map(docToUser);
  } catch (error) {
    throw new Error(
      `Error al obtener usuarios: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}

/**
 * Función para obtener usuarios con filtros
 * @param filters - Los filtros a aplicar
 * @returns {Promise<User[]>} Los usuarios obtenidos
 */
export async function getUsersWithFilters(filters: UserFilters): Promise<User[]> {
  try {
    const constraints: QueryConstraint[] = [];

    // Aplicar ordenamiento
    if (filters.sortBy) {
      const sortOrder = filters.sortOrder || "asc";
      constraints.push(orderBy(filters.sortBy, sortOrder));
    }

    if (filters.active) {
      constraints.push(where("active_imaging", "==", filters.active));
    }

    // Handle multiple types using 'in' query (max 10 types as per Firebase limitation)
    if (filters.types && filters.types.length > 0) {
      // Limit to 10 types as per Firebase limitation
      const typesToQuery = filters.types.slice(0, 10);
      constraints.push(where("type", "in", typesToQuery));
    } else if (filters.type) {
      // Fallback to single type for backward compatibility
      constraints.push(where("type", "==", filters.type));
    }

    if (filters.active_imaging) {
      constraints.push(where("active_imaging", "==", filters.active_imaging));
    }

    // Si es una búsqueda exacta de email, priorizar esa query
    if (filters.search && filters.search.includes("@")) {
      constraints.push(where("email", "==", filters.search.toLowerCase().trim()));
    }

    // Limitar resultados
    // Si hay búsqueda activa pero no es email (filtro cliente), traemos más resultados para aumentar probabilidad de match
    if (filters.search && !filters.search.includes("@")) {
      constraints.push(limit(500));
    } else {
      constraints.push(limit(100));
    }

    // Crear query
    const q = query(getUsersCollection(), ...constraints);
    const querySnapshot = await getDocs(q);

    let users = querySnapshot.docs.map(docToUser);

    // Aplicar búsqueda por texto si se especifica
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const usersResult = querySnapshot.docs.map(docToUser);
      const filteredUsers = usersResult.filter((user) => {
        // Buscar coincidencias en múltiples campos para mayor precisión
        const emailMatch = user.email?.toLowerCase().includes(searchTerm) || false;
        const nameMatch = user.name?.toLowerCase().includes(searchTerm) || false;
        const forenameMatch = user.forename?.toLowerCase().includes(searchTerm) || false;
        const surnameMatch = user.surname?.toLowerCase().includes(searchTerm) || false;
        const fullNameMatch = getUserFullName(user).toLowerCase().includes(searchTerm);

        return emailMatch || nameMatch || forenameMatch || surnameMatch || fullNameMatch;
      });

      return filteredUsers;
    }

    return users;
  } catch (error) {
    throw new Error(
      `Error al obtener usuarios con filtros: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}

/**
 * Función para actualizar un usuario
 * @param userId - El ID del usuario
 * @param userData - Los datos del usuario a actualizar
 * @returns {Promise<User>} El usuario actualizado
 */
export async function updateUser(
  userId: string,
  userData: UpdateUserData
): Promise<User> {
  try {
    const userRef = getUserDoc(userId);
    const updateData = {
      ...userData,
      updatedAt: new Date().toISOString(),
    };

    await updateDoc(userRef, updateData);

    // Obtener el documento actualizado
    const updatedDoc = await getDoc(userRef);
    if (!updatedDoc.exists()) {
      throw new Error("Usuario no encontrado");
    }

    return docToUser(updatedDoc as QueryDocumentSnapshot<DocumentData>);
  } catch (error) {
    throw new Error(
      `Error al actualizar usuario: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}

/**
 * Función para eliminar un usuario
 * @param userId - El ID del usuario
 * @returns {Promise<void>} void
 */
export async function deleteUser(userId: string): Promise<void> {
  try {
    await deleteDoc(getUserDoc(userId));
  } catch (error) {
    throw new Error(
      `Error al eliminar usuario: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}

export async function getUsersPaginated(
  pageSize: number = 10,
  filters?: UserServiceFilters
): Promise<{ users: User[]; total: number; hasMore: boolean }> {
  try {
    const constraints: QueryConstraint[] = [];

    // Aplicar filtros de tipo si existen
    if (filters?.types && filters.types.length > 0) {
      constraints.push(where("type", "in", filters.types.slice(0, 10)));
    } else if (filters?.type) {
      constraints.push(where("type", "==", filters.type));
    }

    if (filters?.active !== undefined) {
      constraints.push(where("active", "==", filters.active));
    }

    // Ordenamiento por defecto
    const sortBy = filters?.sortBy || "created_at";
    const sortOrder = filters?.sortOrder || "desc";
    constraints.push(orderBy(sortBy, sortOrder));

    // Si es una búsqueda exacta de email, priorizar esa query
    if (filters?.search && filters.search.includes("@")) {
      constraints.push(where("email", "==", filters.search.toLowerCase().trim()));
    }

    // Traer uno más para verificar hasMore
    // Aumentar límite si hay búsqueda general para mejorar filtrado en cliente
    const effectiveLimit =
      filters?.search && !filters.search.includes("@") ? 500 : pageSize;
    constraints.push(limit(effectiveLimit + 1));

    const q = query(getUsersCollection(), ...constraints);
    const querySnapshot = await getDocs(q);

    let users = querySnapshot.docs.map(docToUser);

    // Filtrado por búsqueda en memoria (Firestore no soporta búsqueda parcial de texto eficientemente sin índices externos)
    if (filters?.search) {
      const searchTerm = filters.search.toLowerCase();
      users = users.filter((user) => {
        const emailMatch = user.email?.toLowerCase().includes(searchTerm) || false;
        const nameMatch = user.name?.toLowerCase().includes(searchTerm) || false;
        const forenameMatch = user.forename?.toLowerCase().includes(searchTerm) || false;
        const surnameMatch = user.surname?.toLowerCase().includes(searchTerm) || false;
        const fullNameMatch = getUserFullName(user).toLowerCase().includes(searchTerm);

        return emailMatch || nameMatch || forenameMatch || surnameMatch || fullNameMatch;
      });
    }

    const hasMore = users.length > pageSize;
    const paginatedResults = users.slice(0, pageSize);

    return {
      users: paginatedResults,
      total: users.length,
      hasMore,
    };
  } catch (error) {
    throw new Error(
      `Error al obtener usuarios paginados: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}

/**
 * Función para actualizar el último login de un usuario
 * @param userId - El ID del usuario
 * @returns {Promise<void>} void
 */
export async function updateUserLastLogin(userId: string): Promise<void> {
  try {
    const userRef = getUserDoc(userId);
    await updateDoc(userRef, {
      lastLogin: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    throw new Error(
      `Error al actualizar último login: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}

/**
 * Función para obtener usuarios por rol
 * @param role - El rol a buscar
 * @returns {Promise<User[]>} Los usuarios obtenidos
 */
export async function getUsersByRole(role: string): Promise<User[]> {
  try {
    const q = query(getUsersCollection(), where("role", "==", role));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(docToUser);
  } catch (error) {
    throw new Error(
      `Error al obtener usuarios por rol: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}

/**
 * Función para obtener usuarios activos
 * @returns {Promise<User[]>} Los usuarios activos
 */
export async function getActiveUsers(): Promise<User[]> {
  try {
    const q = query(getUsersCollection(), where("status", "==", "active"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(docToUser);
  } catch (error) {
    throw new Error(
      `Error al obtener usuarios activos: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
}
